import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import styled from "styled-components/macro";

import API from "Signups/API";
import { getVerbs } from "Signups/SharedComponents/signupListVerbs";
import { useSignups } from "Signups/state";
import { useIsSignupEditMode } from "Signups/stateHooks";

import FnrIcon from "UI/FnrIcon";
import { FnrScreen, FnrScreensContainer } from "UI/FnrScreens";
import { FnrToggle, FnrToggleChild } from "UI/FnrToggle2";
import { colors } from "UI/colors";
import {
  TabBackground,
  TabBar,
  TabCustomHoverElement,
  TabCustomSelectedElement,
  TabItems,
  TabText,
  tabToggleStyles,
} from "UI/tabBarStyles";

import { getAmountInCents } from "Utilities/Amount/getAmountInCents";

import getIsSignupEligibleForFundIt from "../../utils/getIsSignupEligibleForFundIt";

import { useNewSignupCreationFlow } from "./";
import ChooseType from "./Screens/ChooseType";
import { CostsMoney } from "./Screens/FundIt";
import { Pricing } from "./Screens/FundIt";
import TitleScreen from "./Screens/Title";

export default function NewSignup() {
  const { isSignupEditMode, setIsSignupEditMode } = useIsSignupEditMode();

  const [tab, setTab] = useState("type");

  const [signupListTypeID, setSignupListTypeID] = useState();
  const [title, setTitle] = useState("");
  const [costsMoney, setCostsMoney] = useState(null);
  const [costType, setCostType] = useState(null);
  const [isSuggestedDonation, setIsSuggestedDonation] = useState(null);
  const [amounts, setAmounts] = useState([]);
  const [shouldSwitchToEditMode, setShouldSwitchToEditMode] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [
    {
      hasSignupPayments,
      hasFundIt,
      user: { isAdminOfAnyGroup, isSuperAdmin },
    },
    dispatch,
  ] = useSignups();

  const hasFundItAndSignupPaymentsFeature = hasSignupPayments && hasFundIt;

  const isSignupEligibleForFundIt =
    getIsSignupEligibleForFundIt(signupListTypeID);

  const history = useHistory();
  const { beginItemCreationFlow, setLastCreatedSignupItemID } =
    useNewSignupCreationFlow();

  useEffect(() => {
    dispatch({ type: "Hide Signups List Holder" });
  }, [dispatch]);

  const saveNewSignup = () => {
    setIsLoading(true);

    const amountsInCents = amounts.map(({ amount, ...rest }) => ({
      ...rest,
      amount: Number(getAmountInCents(amount)),
    }));
    API.createSignupList({
      title,
      signupListTypeID,
      hasSignupCost: isSignupEligibleForFundIt ? costsMoney : false,
      costType:
        isSignupEligibleForFundIt && costsMoney && costType ? costType : null,
      isSuggestedDonation: isSignupEligibleForFundIt
        ? isSuggestedDonation
        : false,
      amounts: costType === "PerSignup" ? amountsInCents : null,
    })
      .post()
      .then(({ signupListID, defaultSignupItemID, funds, allSignupLists }) => {
        setLastCreatedSignupItemID(defaultSignupItemID);
        dispatch({
          type: "Update All Signup Lists",
          allSignupLists,
        });
        if (funds) {
          dispatch({
            type: "setFundsList",
            payload: funds,
          });
        }
        beginItemCreationFlow(signupListID);
        setShouldSwitchToEditMode(true);
        // history.push("/signups/" + signupListID + "/items");

        history.push(
          `/signups/${signupListID}/items/${defaultSignupItemID}/info`
        );
      })
      .catch(() => {
        setIsLoading(false);
        alert(
          "Oh no! There was a problem creating the Signup. Please try again or contact help@flocknote.com."
        );
      });
  };

  useEffect(() => {
    if (signupListTypeID) setTab("title");
  }, [signupListTypeID]);

  const { entity } = getVerbs(signupListTypeID);

  useEffect(() => {
    if (shouldSwitchToEditMode && !isSignupEditMode) {
      setIsSignupEditMode(true);
    }
  }, [shouldSwitchToEditMode, isSignupEditMode, setIsSignupEditMode]);

  useEffect(() => {
    if (!isSignupEditMode || shouldSwitchToEditMode) return;
    setIsSignupEditMode(false);
  }, [isSignupEditMode, setIsSignupEditMode, shouldSwitchToEditMode]);

  return (
    <Wrapper data-cy="NewSignup_Wrapper">
      <Nav>
        <Title>Create a Signup</Title>
        <TabBarStyled>
          <FnrToggle
            style={tabToggleStyles}
            CustomSelectedElement={TabCustomSelectedElement}
            CustomHoverElement={TabCustomHoverElement}
          >
            <TabItems>
              <NavTabButton id="type" text="Type" tab={tab} setTab={setTab} />
              <NavBullet>
                <FnrIcon fas fa-chevron-right />
              </NavBullet>
              <NavTabButton
                id="title"
                text="Title"
                tab={tab}
                setTab={setTab}
                disabled={!signupListTypeID}
              />
              {hasFundItAndSignupPaymentsFeature &&
                isAdminOfAnyGroup &&
                (isSignupEligibleForFundIt || !signupListTypeID) && (
                  <>
                    <NavBullet>
                      <FnrIcon fas fa-chevron-right />
                    </NavBullet>
                    <NavTabButton
                      id="fundit"
                      text="Take Payments"
                      tab={tab}
                      setTab={setTab}
                      disabled={title.length < 2}
                    />
                    <NavBullet>
                      <FnrIcon fas fa-chevron-right />
                    </NavBullet>
                    <NavTabButton
                      id="pricing"
                      text="Pricing"
                      tab={tab}
                      setTab={setTab}
                      disabled={!costsMoney && !isSuggestedDonation}
                    />
                  </>
                )}
            </TabItems>
          </FnrToggle>
        </TabBarStyled>
      </Nav>
      <ScreenWraper>
        <FnrScreensContainer
          style={{ alignItems: "flex-start", overflow: "visible" }}
        >
          <FnrScreen show={tab === "type"} side="left">
            <ChooseType
              typeID={signupListTypeID}
              setTypeID={setSignupListTypeID}
              hasSignupPayments={hasSignupPayments}
              hasFundIt={hasFundIt}
              setTab={setTab}
              isSuperAdmin={isSuperAdmin}
              isAdminOfAnyGroup={isAdminOfAnyGroup}
            />
          </FnrScreen>
          <FnrScreen
            show={tab === "title"}
            side={tab === "type" ? "right" : "left"}
          >
            <TitleScreen
              title={title}
              setTitle={setTitle}
              willShowFundItNext={
                hasFundItAndSignupPaymentsFeature &&
                isSignupEligibleForFundIt &&
                isAdminOfAnyGroup
              }
              setTab={setTab}
              saveNewSignup={saveNewSignup}
              isLoading={isLoading}
            />
          </FnrScreen>
          {hasFundItAndSignupPaymentsFeature && isSignupEligibleForFundIt && (
            <>
              <FnrScreen
                show={tab === "fundit"}
                side={tab === "pricing" ? "left" : "right"}
              >
                <CostsMoney
                  setTab={setTab}
                  setCostsMoney={setCostsMoney}
                  setIsSuggestedDonation={setIsSuggestedDonation}
                  saveNewSignup={saveNewSignup}
                  isLoading={isLoading}
                  entity={entity}
                />
              </FnrScreen>
              <FnrScreen show={tab === "pricing"} side="right">
                <Pricing
                  isSuggestedDonation={isSuggestedDonation}
                  amounts={amounts}
                  setAmounts={setAmounts}
                  costType={costType}
                  setCostType={setCostType}
                  saveNewSignup={saveNewSignup}
                  isLoading={isLoading}
                  entity={entity}
                  listTypeID={signupListTypeID}
                />
              </FnrScreen>
            </>
          )}
        </FnrScreensContainer>
      </ScreenWraper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 20px;
  width: 480px;
  margin: 0 auto;
  box-sizing: border-box;
`;
const Title = styled.div`
  font-size: 24px;
  font-weight: 400;
  color: ${colors.fnTabSelectedGray};
  padding-top: 24px;

  @media (max-width: 730px) {
    padding-top: 32px; // to fix overlapping with "All Signups button"
  }
`;
const Nav = styled.div``;
const TabBarStyled = styled(TabBar)`
  margin-left: -16px;
`;
const NavBullet = styled.div`
  font-size: 10px;
  align-self: center;
  margin-bottom: 4px;
  color: ${colors.fnGray};
`;
const ScreenWraper = styled.div``;

function NavTabButton({ id, text, disabled, tab, setTab }) {
  const matches = tab === id;

  const onClick = () => setTab(id);

  return (
    <NavButton>
      <FnrToggleChild
        selected={matches}
        style={{ flexGrow: 0 }}
        disabled={disabled}
      >
        <TabBackground disabled={disabled} onClick={disabled ? null : onClick}>
          <TabText>{text}</TabText>
        </TabBackground>
      </FnrToggleChild>
    </NavButton>
  );
}
const NavButton = styled.div`
  padding: 0 18px;

  &:first-child {
    padding-left: 0;
  }
`;

export const Subtitle = styled.div`
  font-size: 18px;
  font-weight: 400;
  margin-bottom: 24px;
  margin-top: 12px;
  color: ${colors.fnTabSelectedGray};
`;
export const Description = styled.div`
  font-weight: 400;
  color: ${colors.fnGray};
`;
